// componetnts
import { useCallback, useContext, useEffect, useMemo, useState } from "react";

// components
import Spinner from "../../common/Spinner";
import Modal from "../../common/Modal";
import TextInput, { InputType } from "../../forms/textInput";
import PrimaryButton from "../../common/PrimaryButton";

// styles
import styles from "./CashoutModal.module.scss";

import {
  SINGLE_VIEW,
  SINGLE_VIEW_PLATFORM,
  MANUAL,
  MANUAL_BANK_TRANSFER,
  CHOOSE_TRANSFER_METHOD,
  SINGLE_VIEW_TRANSER_HEADER,
  BANK_TRANSFER_HEADER,
  TOTAL_DUE,
  BANK_ACCOUNT_NUMBER,
  BENEFICIARY_NAME,
  THE_BANK,
  ADDRESS,
  UNAVAILABLE,
  RIYAL,
  THE_AMOUNT_TO_BE_TRANSFERED,
  COMPLETE_CONVERSION,
  BANK_ACCOUNT_NOT_ADDED,
  TRANSFER_TO_REFERENCE_NUMBER,
  WAITING_FOR_CONVERSION,
  CONVERSION_IN_PROGRESS,
  SUCCESS_MESSAGE,
  MOYASAR,
  MOYASAR_PLATFORM,
  TABBY,
  TABBY_PLATFORM,
  TABBY_TRANSER_HEADER,
  NEO,
  NEO_PLATFORM,
  NEO_TRANSER_HEADER,
  MOYASAR_TRANSER_HEADER,
  CHOOSE_REFUND_METHOD,
  OTHER_REFUND_METHODS,
  TAMARA,
  TAMARA_PLATFORM,
  TAMARA_TRANSER_HEADER,
} from "./Constants";
// context and providers
import { ContractContext } from "../../../providers/contract";

// APIS
import {
  checkUserBank,
  makeCashOutPayment,
  revertTransaction,
} from "../../../setup/api/services/payment";
import { getUserDetailsById } from "../../../setup/api/services/user";
import {
  BankResponseData,
  ICashoutRequest,
  ICashoutResponse,
} from "../../../setup/api/services/payment/models";
import { IUserAddressResponse } from "../../../setup/api/services/user/models";
import { COUNTRY } from "../../../pages/userStats/constant";
import { AlertColor } from "@mui/material";
import { IContractActionRequestBody } from "../../../setup/api/services/contractActions/models";
import {
  EContractType,
  ESenderRole,
  ETrasactionsType,
} from "../../../setup/api/services/contract/models";
import { EContractActions } from "../../../setup/api/services/contractActions/enum";
import {
  ACTION_UNDER_PROCESSING,
  CANNOT_MAKE_THIS_CALL,
} from "../../../pages/contract/constants";
import CustomizedSnackbars from "../../customizedSnackbars";
import { doAction } from "../../../setup/api/services/contractActions";
import { ResolveState, ampli } from "../../../ampli";
import { ECONTRACT_STATUS } from "../../../enums";
import useLocalStorage from "../../../hooks/useLocalStorage";
import { IUser } from "../../../setup/api/services/auth/models";
import classNames from "classnames";

interface Props {
  showModal: boolean;
  closeModal: () => void;
  userType: "seller" | "buyer";
  checkCashoutStatus: () => void;
  refreshContractDetails: () => void;
}

enum SElECTION {
  BANK = "BANK",
  SINGLE_VIEW = "SINGLE_VIEW",
  TABBY = "TABBY",
  NEO = "NEO",
  MOYASAR = "MOYASAR",
  TAMARA = "TAMARA",
  NONE = "NONE",
}

const CashoutModal = ({
  showModal,
  closeModal,
  userType,
  checkCashoutStatus,
  refreshContractDetails,
}: Props) => {
  const { contract } = useContext(ContractContext);
  const [isPending, setIsPending] = useState<boolean>(false);
  const [showPending, setShowPending] = useState<boolean>(false);
  const [showSuccess, setShowSuccess] = useState<boolean>(false);
  const [selectionType, setSelectionType] = useState<SElECTION>(SElECTION.NONE);
  const [bankDetails, setBankDetails] = useState<
    BankResponseData | undefined
  >();
  const [address, setUserAddress] = useState<
    IUserAddressResponse["data"] | undefined
  >(undefined);

  const [amount, setAmount] = useState<number | string>();

  const userId = useMemo(() => {
    if (contract?.type === EContractType.QUICK_TRANSACTION) {
      return userType === "buyer"
        ? contract?.customer?.id
        : contract?.provider?.id;
    } else {
      return userType === "seller"
        ? contract?.parties?.find((party) => party.role === "PROVIDER")?.userId
        : contract?.parties?.find((party) => party.role === "CUSTOMER")?.userId;
    }
  }, [
    contract?.customer?.id,
    contract?.provider?.id,
    userType,
    contract?.parties,
    contract?.type,
  ]);

  const [admin] = useLocalStorage<IUser | null>("userInfo", null);

  // snackbar
  const [snackBarOpen, setSnackBarOpen] = useState<boolean>(false);
  const [snackBarColor, setSnackBarColor] = useState<AlertColor>();
  const [snackBarTitle, setSnackBarTitle] = useState<string>("");

  const subTotal = useMemo(() => {
    if (!contract) return 0;
    return (
      (contract?.hasVoucher
        ? contract?.originalItemPrice || contract?.itemPrice
        : contract?.itemPrice) + (contract?.shipment?.fee || 0)
    );
  }, [contract]);

  const handleClose = useCallback(() => {
    setIsPending(false);
    setBankDetails(undefined);
    setUserAddress(undefined);
    setShowPending(false);
    closeModal();

    setSelectionType((prevSelection) => {
      if (prevSelection !== SElECTION.BANK && prevSelection !== SElECTION.NONE)
        setTimeout(() => window.location.reload(), 1000);
      return SElECTION.NONE;
    });
  }, [closeModal]);

  const handleSelection = useCallback(
    async (selection: SElECTION) => {
      setIsPending(true);
      try {
        const bankDetails = await checkUserBank(userId);
        setBankDetails(bankDetails?.data);
      } catch (error) {
        console.error(error);
      }
      try {
        const result = await getUserDetailsById(Number(userId));
        setUserAddress(result?.data?.profile?.addresses[0]);
        setSelectionType(selection);
      } catch (error) {
        setSelectionType(selection);
        console.error(error);
      }
      setIsPending(false);
    },
    [userId]
  );

  const setSnackBarStates = useCallback((title: string, color: AlertColor) => {
    setSnackBarOpen(true);
    setSnackBarColor(color);
    setSnackBarTitle(title);
  }, []);

  const doContractAction = useCallback(
    async (res?: ICashoutResponse) => {
      if (res) {
        setSnackBarStates(ACTION_UNDER_PROCESSING, "success");
      } else {
        setSnackBarStates(CANNOT_MAKE_THIS_CALL, "error");
      }
    },
    [setSnackBarStates]
  );

  const sendResolveEscalationEvent = useCallback(
    (
      currentAction: EContractActions,
      buyerId: string,
      contractId: string,
      sellerId: string
    ) => {
      if (
        currentAction ===
          EContractActions.ADMIN_RESOLVE_ESCALATION_REFUND_CUSTOMER ||
        currentAction ===
          EContractActions.ADMIN_RESOLVE_ESCALATION_RELEASE_PROVIDER
      ) {
        let resolveState = ResolveState.Refund;
        if (
          currentAction ===
          EContractActions.ADMIN_RESOLVE_ESCALATION_RELEASE_PROVIDER
        ) {
          resolveState = ResolveState.Release;
        }
        const properties = {
          adminResolveDate: String(new Date()),
          buyerId: buyerId,
          contractId: contractId,
          resolveState: resolveState,
          sellerId: sellerId,
        };
        try {
          ampli && ampli.adminResolveEscalation(properties);
        } catch (error) {
          console.error(error);
        }
      }
    },
    []
  );

  const sendRefundReleaseEvent = useCallback(
    (
      currentAction: EContractActions,
      buyerId: string,
      contractId: string,
      sellerId: string
    ) => {
      if (
        currentAction === EContractActions.ADMIN_CLOSE_REFUND_CUSTOMER ||
        currentAction === EContractActions.ADMIN_CLOSE_RELEASE_FUND_TO_PROVIDER
      ) {
        const properties = {
          adminRefundDate: String(new Date()),
          buyerId: buyerId,
          contractId: contractId,
          sellerId: sellerId,
        };
        try {
          if (currentAction === EContractActions.ADMIN_CLOSE_REFUND_CUSTOMER)
            ampli.adminRefundBuyer(properties);
          if (
            currentAction ===
            EContractActions.ADMIN_CLOSE_RELEASE_FUND_TO_PROVIDER
          )
            ampli.adminReleaseSeller(properties);
        } catch (error) {
          console.error(error);
        }
      }
    },
    []
  );

  const onConfirm = useCallback(async () => {
    if (contract && userId) {
      setShowPending(true);
      // this body is used for cashout and revert
      const cashoutBody: ICashoutRequest = {
        amount: 0,
        referenceTransactionId: "",
      };

      let receiver: string | undefined = undefined;
      if (contract.type === EContractType.BROKERAGE_CONTRACT) {
        receiver =
          userType === "seller"
            ? String(contract.partiesProfiles?.PROVIDER[0].userIdentification)
            : String(contract.partiesProfiles?.CUSTOMER[0].userIdentification);
      }
      // a milestone has no receiverId of its own; the party being paid is
      // addressed by its partiesProfiles id, as the per-party actions do
      if (contract.type === EContractType.MILESTONE_CONTRACT) {
        const profiles =
          userType === "buyer"
            ? contract.partiesProfiles?.CUSTOMER
            : contract.partiesProfiles?.PROVIDER;
        const profileId = profiles?.find(
          (profile) => String(profile?.userIdentification) === String(userId)
        )?.id;
        if (profileId !== undefined) receiver = String(profileId);
      }

      // this is the body for the normal flow with contract actions
      const contractActionBody: IContractActionRequestBody = {
        contractId: contract.id,
        userId: admin?.id || "",
        actorRole: ESenderRole.ADMIN,
        receiverId: receiver || contract?.receiverId,
        senderId: contract.senderId,
        contractAction:
          userType === "buyer"
            ? EContractActions.ADMIN_CLOSE_REFUND_CUSTOMER
            : contract?.status === ECONTRACT_STATUS.RESOLVED_RELEASE_PROVIDER ||
              contract?.status ===
                ECONTRACT_STATUS.WAITING_FOR_SETTLEMENT_RESOLVE
            ? EContractActions.ADMIN_CLOSE_RELEASE_FUND_TO_PROVIDER
            : EContractActions.ADMIN_COMPLETE_RELEASE_FUND_TO_PROVIDER,
        transactionId: `${amount}`,
      };

      if (
        contract.type === EContractType.BROKERAGE_CONTRACT &&
        userType === "seller"
      ) {
        contractActionBody.transactionParties = {
          beneficiaryId: String(
            contract.partiesProfiles?.PROVIDER[0].userIdentification
          ),
          fromUserId: String(
            contract.partiesProfiles?.CUSTOMER[0].userIdentification
          ),
        };
      }

      if (amount && contract?.transactions) {
        cashoutBody.referenceTransactionId = contract?.transactions
          ? contract?.transactions[0]?.referenceTransactionId
          : "";
        cashoutBody.amount = +amount || 0;
        if (contract?.type === EContractType.COMPLEX_CONTRACT) {
          cashoutBody.assignedBeneficiaryId = contract?.receiverId;
          cashoutBody.assignedBeneficiaryName =
            contract?.partiesProfiles?.PROVIDER?.find(
              (provider) => provider?.id == +(contract?.receiverId || 0)
            )?.profile?.fullName;
        } else if (userType !== "buyer") {
          cashoutBody.amount = +amount || 0;

          if (contract?.type === EContractType.BROKERAGE_CONTRACT) {
            cashoutBody.assignedBeneficiaryId =
              contract?.parties?.find((party) => party.role === "PROVIDER")
                ?.userId || "";
            cashoutBody.assignedBeneficiaryName =
              contract?.partiesProfiles?.PROVIDER?.find(
                (provider) =>
                  String(provider?.id) === cashoutBody.assignedBeneficiaryId ||
                  String(provider.userIdentification) ===
                    cashoutBody.assignedBeneficiaryId
              )?.profile?.fullName || "";
          } else {
            cashoutBody.assignedBeneficiaryId = `${contract?.provider?.id}`;
            cashoutBody.assignedBeneficiaryName =
              contract?.provider?.profile?.fullName;
          }
        }
      }

      try {
        let cashoutRes;
        let revertRes;
        let normalRes;
        if (amount && contract?.transactions) {
          if (
            userType === "buyer" &&
            (selectionType === SElECTION.TABBY ||
              selectionType === SElECTION.NEO ||
              selectionType === SElECTION.MOYASAR ||
              selectionType === SElECTION.TAMARA)
          ) {
            revertRes = await revertTransaction(cashoutBody);
          } else if (
            userType === "seller" &&
            selectionType === SElECTION.SINGLE_VIEW
          ) {
            cashoutRes = await makeCashOutPayment(cashoutBody);
          }
        }

        if (cashoutRes) {
          const referenceTransactionId =
            cashoutRes?.data?.referenceTransactionId;
          const transactionId = cashoutRes?.data?.transactionId;
          setSnackBarStates(
            cashoutRes?.data?.statusDescription || ACTION_UNDER_PROCESSING,
            cashoutRes?.data?.status !== "FAILED" ? "success" : "error"
          );
          contractActionBody.referenceTransactionId = referenceTransactionId;
          contractActionBody.transactionId = transactionId;
          if (cashoutRes?.data?.status !== "FAILED") checkCashoutStatus();
          refreshContractDetails();
        } else if (revertRes) {
          const referenceTransactionId =
            revertRes?.data?.referenceTransactionId;
          const transactionId = revertRes?.data?.transactionId;
          setSnackBarStates(ACTION_UNDER_PROCESSING, "success");
          contractActionBody.transactionId = transactionId;
          contractActionBody.referenceTransactionId = referenceTransactionId;
          refreshContractDetails();
        } else {
          if (selectionType === SElECTION.BANK) {
            normalRes = await doAction(contractActionBody);
            setSnackBarStates(ACTION_UNDER_PROCESSING, "success");
            refreshContractDetails();
          }
        }

        sendResolveEscalationEvent(
          EContractActions.ADMIN_CLOSE_RELEASE_FUND_TO_PROVIDER,
          contract?.receiverId || "",
          contract.shortId,
          contract?.senderId
        );

        sendRefundReleaseEvent(
          EContractActions.ADMIN_CLOSE_REFUND_CUSTOMER,
          contract?.receiverId || "",
          contract.shortId,
          contract?.senderId
        );
        doContractAction(cashoutRes || revertRes || normalRes);
        setShowSuccess(true);
        setShowPending(false);
      } catch (error) {
        setSnackBarStates(CANNOT_MAKE_THIS_CALL, "error");
        doContractAction(undefined);
        setShowSuccess(false);
        setShowPending(false);
        handleClose();
      }
    }
  }, [
    admin?.id,
    amount,
    checkCashoutStatus,
    contract,
    doContractAction,
    handleClose,
    refreshContractDetails,
    selectionType,
    sendRefundReleaseEvent,
    sendResolveEscalationEvent,
    setSnackBarStates,
    userId,
    userType,
  ]);

  useEffect(() => {
    if (showSuccess) {
      setTimeout(() => {
        setShowSuccess(false);
        handleClose();
      }, 5000);
    }
  }, [handleClose, showSuccess]);

  const renderSelection = useCallback(() => {
    return (
      <div className={styles["container"]}>
        {isPending ? (
          <Spinner />
        ) : (
          <div
            style={{ width: "100%" }}
            className={classNames({
              [styles["horizontal_section_container"]]:
                contract?.status !== ECONTRACT_STATUS.RESOLVED_REFUND_CUSTOMER,
            })}>
            {contract?.status === ECONTRACT_STATUS.RESOLVED_REFUND_CUSTOMER &&
              contract?.transactions?.some(
                (transaction) =>
                  transaction?.type === ETrasactionsType.CUSTOMER_PAY &&
                  transaction?.paymentMethod === "TABBY"
              ) && (
                <div
                  onClick={() => handleSelection(SElECTION.TABBY)}
                  className={styles["wide_section"]}>
                  <img
                    className={styles["avatar_img"]}
                    src="/assets/images/svg/tabby.svg"
                  />
                  <div>
                    <h4>{TABBY}</h4>
                    <p className={styles["p2"]}>{TABBY_PLATFORM}</p>
                  </div>
                </div>
              )}
            {contract?.status === ECONTRACT_STATUS.RESOLVED_REFUND_CUSTOMER &&
              contract?.transactions?.some(
                (transaction) =>
                  transaction?.type === ETrasactionsType.CUSTOMER_PAY &&
                  transaction?.paymentMethod === "NEO"
              ) && (
                <div
                  onClick={() => handleSelection(SElECTION.NEO)}
                  className={styles["wide_section"]}>
                  <img
                    className={styles["avatar_img"]}
                    src="/assets/images/svg/NEO.svg"
                  />
                  <div>
                    <h4>{NEO}</h4>
                    <p className={styles["p2"]}>{NEO_PLATFORM}</p>
                  </div>
                </div>
              )}
            {contract?.status === ECONTRACT_STATUS.RESOLVED_REFUND_CUSTOMER &&
              contract?.transactions?.some(
                (transaction) =>
                  transaction?.type === ETrasactionsType.CUSTOMER_PAY &&
                  transaction?.paymentMethod === "TAMARA"
              ) && (
                <div
                  onClick={() => handleSelection(SElECTION.TAMARA)}
                  className={styles["wide_section"]}>
                  <img
                    className={styles["avatar_img"]}
                    src="/assets/images/svg/Tamara.svg"
                  />
                  <div>
                    <h4>{TAMARA}</h4>
                    <p className={styles["p2"]}>{TAMARA_PLATFORM}</p>
                  </div>
                </div>
              )}
            {contract?.status === ECONTRACT_STATUS.RESOLVED_REFUND_CUSTOMER &&
              contract?.transactions?.some(
                (transaction) =>
                  transaction?.type === ETrasactionsType.CUSTOMER_PAY &&
                  transaction?.paymentMethod === "CARD"
              ) && (
                <div
                  onClick={() => handleSelection(SElECTION.MOYASAR)}
                  className={styles["wide_section"]}>
                  <img
                    style={{ height: "48px" }}
                    className={styles["avatar_img"]}
                    src="/assets/images/svg/moyasar.svg"
                  />
                  <div>
                    <h4>{MOYASAR}</h4>
                    <p className={styles["p2"]}>{MOYASAR_PLATFORM}</p>
                  </div>
                </div>
              )}
            {contract?.status === ECONTRACT_STATUS.RESOLVED_REFUND_CUSTOMER && (
              <h3 style={{ marginTop: "32px", marginBottom: "16px" }}>
                {OTHER_REFUND_METHODS}
              </h3>
            )}
            <div className={styles["payment_options"]}>
              <div
                onClick={() => handleSelection(SElECTION.BANK)}
                className={styles["section"]}>
                <img
                  className={styles["avatar_img"]}
                  src="/assets/images/svg/bank_transfer.svg"
                />
                <h4>{MANUAL}</h4>
                <p className={styles["p2"]}>{MANUAL_BANK_TRANSFER}</p>
              </div>
              {(contract?.status ===
                ECONTRACT_STATUS.RESOLVED_RELEASE_PROVIDER ||
                contract?.status ===
                  ECONTRACT_STATUS.WAITING_FOR_SETTLEMENT_CLOSE ||
                contract?.status ===
                  ECONTRACT_STATUS.WAITING_FOR_SETTLEMENT_RESOLVE ||
                contract?.status === ECONTRACT_STATUS.ITEM_ACCEPTED ||
                contract?.status ===
                  ECONTRACT_STATUS.RESOLVED_REFUND_CUSTOMER) && (
                <div
                  onClick={() => handleSelection(SElECTION.SINGLE_VIEW)}
                  className={styles["section"]}>
                  <img
                    className={styles["avatar_img"]}
                    src="/assets/images/svg/single_view.svg"
                  />
                  <h4>{SINGLE_VIEW}</h4>
                  <p className={styles["p2"]}>{SINGLE_VIEW_PLATFORM}</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }, [contract?.status, contract?.transactions, handleSelection, isPending]);

  const isDisabled = useMemo(() => {
    if (selectionType === SElECTION.BANK) {
      if (bankDetails) return false;
      else return true;
    }
    if (
      selectionType === SElECTION.SINGLE_VIEW ||
      selectionType === SElECTION.TABBY ||
      selectionType === SElECTION.NEO ||
      selectionType === SElECTION.MOYASAR ||
      selectionType === SElECTION.TAMARA
    ) {
      if (
        ((!bankDetails || !address) &&
          contract?.status !== ECONTRACT_STATUS.RESOLVED_REFUND_CUSTOMER) ||
        !amount
      ) {
        return true;
      }
      if (Number(amount) > subTotal) return true;
    }
    return false;
  }, [address, amount, bankDetails, contract?.status, selectionType, subTotal]);

  const renderSnackBar = () => {
    return snackBarOpen ? (
      <CustomizedSnackbars
        open={snackBarOpen}
        title={snackBarTitle}
        type={snackBarColor}
      />
    ) : null;
  };

  const renderAccountDetails = useCallback(() => {
    return (
      <>
        <div className={styles["account_container"]}>
          {!bankDetails &&
            contract?.status !== ECONTRACT_STATUS.RESOLVED_REFUND_CUSTOMER && (
              <div className={styles["info_container"]}>
                <div className={styles["img_container"]}>
                  <img src={"/assets/images/svg/exclamation.svg"} />
                </div>
                <p>{BANK_ACCOUNT_NOT_ADDED}</p>
              </div>
            )}
          <div className={styles["warning_message"]}></div>
          <div className={styles["details"]}>
            <p>{TOTAL_DUE}</p>
            <p className={styles["detail_amount"]}>
              {subTotal?.toFixed(2)}
              <span>{RIYAL}</span>
            </p>
          </div>
          {selectionType !== SElECTION.TABBY &&
            selectionType !== SElECTION.NEO &&
            selectionType !== SElECTION.MOYASAR &&
            selectionType !== SElECTION.TAMARA && (
              <div className={styles["details"]}>
                <p>{BANK_ACCOUNT_NUMBER}</p>
                {bankDetails ? (
                  <p className={styles["detail_desc"]}>{bankDetails?.iban}</p>
                ) : (
                  <p className={styles["pendingText"]}>{UNAVAILABLE}</p>
                )}
              </div>
            )}
          {selectionType !== SElECTION.TABBY &&
            selectionType !== SElECTION.NEO &&
            selectionType !== SElECTION.MOYASAR &&
            selectionType !== SElECTION.TAMARA && (
              <div className={styles["details"]}>
                <p>{BENEFICIARY_NAME}</p>
                {bankDetails ? (
                  <p className={styles["detail_desc"]}>
                    {bankDetails?.beneficiaryName}
                  </p>
                ) : (
                  <p className={styles["pendingText"]}>{UNAVAILABLE}</p>
                )}
              </div>
            )}
          {selectionType !== SElECTION.TABBY &&
            selectionType !== SElECTION.NEO &&
            selectionType !== SElECTION.MOYASAR &&
            selectionType !== SElECTION.TAMARA && (
              <div className={styles["details"]}>
                <p>{THE_BANK}</p>
                {bankDetails ? (
                  <p className={styles["detail_desc"]}>
                    {bankDetails?.bank?.arabicName}
                  </p>
                ) : (
                  <p className={styles["pendingText"]}>{UNAVAILABLE}</p>
                )}
              </div>
            )}
          {selectionType !== SElECTION.TABBY &&
            selectionType !== SElECTION.NEO &&
            selectionType !== SElECTION.MOYASAR &&
            selectionType !== SElECTION.TAMARA && (
              <div className={styles["details"]}>
                <p>{ADDRESS}</p>
                {address ? (
                  <p className={styles["detail_desc"]}>
                    {COUNTRY[address?.countryCode]} - {address?.city} -
                    {address?.district} - {address?.street} -{" "}
                    {address?.buildingNo} -{address?.postalCode}
                  </p>
                ) : (
                  <p className={styles["pendingText"]}>{UNAVAILABLE}</p>
                )}
              </div>
            )}
        </div>
        <div className={styles["text_area"]}>
          <TextInput
            handleChange={(event) => setAmount(event.target.value)}
            type={
              selectionType !== SElECTION.BANK
                ? InputType.NUMBER
                : InputType.DEFAULT
            }
            defaultValue={amount}
            placeholder={
              selectionType !== SElECTION.BANK
                ? THE_AMOUNT_TO_BE_TRANSFERED
                : TRANSFER_TO_REFERENCE_NUMBER
            }
          />
        </div>
        <div className={styles["button_container"]}>
          <PrimaryButton disabled={isDisabled} onClick={onConfirm}>
            {COMPLETE_CONVERSION}
          </PrimaryButton>
        </div>
      </>
    );
  }, [
    address,
    amount,
    bankDetails,
    contract?.status,
    isDisabled,
    onConfirm,
    selectionType,
    subTotal,
  ]);

  if (showPending) {
    return (
      <Modal size={"small"} visible={showModal} onCancel={handleClose}>
        <div className={styles["pending_container"]}>
          <img src="/assets/images/svg/pending.svg" />
          <h1>{CONVERSION_IN_PROGRESS}</h1>
          <p className={styles["p1"]}>{WAITING_FOR_CONVERSION}</p>
        </div>
      </Modal>
    );
  }

  if (showSuccess) {
    return (
      <Modal size={"small"} visible={showModal} onCancel={handleClose}>
        <div className={styles["success_container"]}>
          <img src="/assets/images/svg/Success_tick.svg" />
          <h1>{SUCCESS_MESSAGE}</h1>
        </div>
      </Modal>
    );
  }

  return (
    <Modal size={"small"} visible={showModal} onCancel={handleClose}>
      {renderSnackBar()}
      {selectionType === SElECTION.NONE && (
        <h1>
          {contract?.status === ECONTRACT_STATUS.RESOLVED_REFUND_CUSTOMER
            ? CHOOSE_REFUND_METHOD
            : CHOOSE_TRANSFER_METHOD}
        </h1>
      )}
      {selectionType === SElECTION.SINGLE_VIEW && (
        <h1>{SINGLE_VIEW_TRANSER_HEADER}</h1>
      )}
      {selectionType === SElECTION.TABBY && <h1>{TABBY_TRANSER_HEADER}</h1>}
      {selectionType === SElECTION.NEO && <h1>{NEO_TRANSER_HEADER}</h1>}
      {selectionType === SElECTION.TAMARA && <h1>{TAMARA_TRANSER_HEADER}</h1>}
      {selectionType === SElECTION.MOYASAR && <h1>{MOYASAR_TRANSER_HEADER}</h1>}
      {selectionType === SElECTION.BANK && <h1>{BANK_TRANSFER_HEADER}</h1>}
      {selectionType === SElECTION.NONE && <>{renderSelection()}</>}
      {selectionType !== SElECTION.NONE && <>{renderAccountDetails()}</>}
    </Modal>
  );
};

export default CashoutModal;
